import yaml from "js-yaml";
import type { ProfileChannel, ProfileData, ProfilePublishValues } from "@/types/profile";
import { getChannelReleaseMode, getNormalizedPublishJobTypes } from "@/utilities/profiles/channelFormUtils";
import { getReviewChannelSections } from "@/utilities/profiles/profileReviewUtils";

const YAML_DUMP_OPTIONS: yaml.DumpOptions = { lineWidth: -1, noRefs: true, sortKeys: false };

type YamlMapValue = null | { Event?: { Enabled?: boolean } };

const publishValueKeys = (values: ProfilePublishValues | undefined): string[] => {
  if (!values) return [];
  if (Array.isArray(values)) return values.map(String);
  return Object.keys(values);
};

/** Converts list/map publish values to YAML map syntax (`dev:` with null value). */
const toYamlNullMap = (values: ProfilePublishValues | undefined): Record<string, null> | undefined => {
  const keys = publishValueKeys(values);
  if (!keys.length) return undefined;
  return Object.fromEntries(keys.map((key) => [key, null]));
};

type YamlJobTypeValue = null | { PreBuilt?: boolean };

const toYamlJobType = (values: ProfilePublishValues | undefined): Record<string, YamlJobTypeValue> | undefined => {
  if (!values) return undefined;

  if (Array.isArray(values)) {
    return Object.fromEntries(values.map((key) => [String(key), null]));
  }

  const map: Record<string, YamlJobTypeValue> = {};
  Object.entries(values).forEach(([key, entry]) => {
    if (entry === null || entry === undefined) {
      map[key] = null;
      return;
    }
    if (typeof entry === "object" && (entry as { PreBuilt?: boolean }).PreBuilt === true) {
      map[key] = { PreBuilt: true };
      return;
    }
    map[key] = null;
  });

  return Object.keys(map).length > 0 ? map : undefined;
};

const RELEASE_YAML_STAGES = ["Preview", "Live"] as const;

const toYamlRequiredImages = (
  channel: ProfileChannel,
  stage: (typeof RELEASE_YAML_STAGES)[number]
): Record<string, YamlMapValue> | undefined => {
  const raw = channel.Release?.[stage]?.RequiredImages;
  if (!raw) return undefined;

  const keys = publishValueKeys(raw);
  if (!keys.length) return undefined;

  const map: Record<string, YamlMapValue> = {};
  keys.forEach((jobType) => {
    if (!Array.isArray(raw)) {
      const entry = raw[jobType];
      if (
        typeof entry === "object" &&
        entry !== null &&
        ((entry as { Event?: { Enabled?: boolean } }).Event?.Enabled === false ||
          (entry as { SendEvent?: boolean }).SendEvent === false)
      ) {
        map[jobType] = { Event: { Enabled: false } };
        return;
      }
    }
    map[jobType] = null;
  });

  return map;
};

const buildYamlRelease = (channel: ProfileChannel): Record<string, unknown> | undefined => {
  const release: Record<string, unknown> = {};

  if (channel.Release?.MaxBuilds !== undefined) {
    release.MaxBuilds = channel.Release.MaxBuilds;
  }

  const allowedImages = toYamlNullMap(channel.Release?.AllowedImages);
  if (allowedImages) {
    release.AllowedImages = allowedImages;
  }

  RELEASE_YAML_STAGES.forEach((stage) => {
    const requiredImages = toYamlRequiredImages(channel, stage);
    if (requiredImages) {
      release[stage] = { RequiredImages: requiredImages };
    }
  });

  // if (channel.PromotionAllowed !== undefined) {
  //   release.PromotionAllowed = channel.PromotionAllowed;
  // }

  return Object.keys(release).length > 0 ? release : undefined;
};

/** Maps internal channel state to the job profile YAML schema field names and shapes. */
export const toProfileYamlChannel = (channel: ProfileChannel): Record<string, unknown> => {
  const payload: Record<string, unknown> = { Name: channel.Name };

  if (channel.Parent) {
    payload.Parent = channel.Parent;
  }

  const publish: Record<string, unknown> = {};
  const environment = toYamlNullMap(channel.Publish?.Environment);
  const jobType = toYamlJobType(getNormalizedPublishJobTypes(channel));
  if (environment) publish.Environment = environment;
  if (jobType) publish.JobType = jobType;
  if (Object.keys(publish).length > 0) {
    payload.Publish = publish;
  }

  // if (channel.TTL !== undefined) {
  //   payload.TTL = channel.TTL;
  // }

  payload.ReleaseMode = getChannelReleaseMode(channel);

  const release = buildYamlRelease(channel);
  if (release) {
    payload.Release = release;
  }

  // if (channel.SideBySide !== undefined) {
  //   payload.Side_by_side = channel.SideBySide;
  // }

  payload.Enabled = channel.Enabled !== false;

  return payload;
};

export const orderChannelsForYaml = (channels: ProfileChannel[]): ProfileChannel[] =>
  getReviewChannelSections(channels);

export const toProfileYamlPayload = (profileData: ProfileData): Record<string, unknown> => {
  const profile: Record<string, unknown> = { PLC: profileData.Profile.PLC };

  if (profileData.Profile.ReleaseType?.length) {
    profile.ReleaseType = profileData.Profile.ReleaseType;
  }
  if (profileData.Profile.Platform?.length) {
    profile.Platform = profileData.Profile.Platform;
  }
  if (profileData.Profile.Owners?.length) {
    profile.Owners = profileData.Profile.Owners.map((owner) => ({
      Email: owner.Email,
      Name: owner.Name
    }));
  }

  return {
    Profile: profile,
    Channels: orderChannelsForYaml(profileData.Channels).map(toProfileYamlChannel)
  };
};

export const formatProfileDataAsYaml = (profileData: ProfileData): string =>
  yaml.dump(toProfileYamlPayload(profileData), YAML_DUMP_OPTIONS);

export const formatProfileChannelsAsYaml = (channels: ProfileChannel[]): string => {
  const ordered = orderChannelsForYaml(channels);
  if (!ordered.length) return "";
  return yaml.dump({ Channels: ordered.map(toProfileYamlChannel) }, YAML_DUMP_OPTIONS);
};

export const formatProfileChannelAsYaml = (channel: ProfileChannel | undefined): string => {
  if (!channel) return "";
  return yaml.dump(toProfileYamlChannel(channel), YAML_DUMP_OPTIONS);
};
